import { Controller, useFormContext, ControllerRenderProps, FieldValues } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { FC, KeyboardEvent, useRef } from "react";
import { Typography } from "@kaltura/ds-react-components";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import styled from "@emotion/styled";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";
import FormLabelWithDescription from "../../form/form-label-with-description/FormLabelWithDescription";

/**
 * data structure for room templates
 */
export interface Template {
    /**
     * template entry id
     */
    id: string;
    /**
     * template name (translated)
     */
    name: string;
    /**
     * template description (translated)
     */
    description?: string;
    /**
     * name of illustration component to use for this template.
     * a matching component should exist in the illustrations map.
     * if not defined, a custom illustration will be used
     */
    illustration?: string;
}

interface TemplateSelectionProps {
    labelSingle: string;
    labelMultiple: string;
    templates: Template[];
    illustrationsMap: Record<string, FC>;
    defaultIllustration: FC;
}

const StyledLabel = styled(FormLabelWithDescription)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    display: "block",
}));

const StyledGrid = styled("div")<{ singleItem?: boolean }>(({ theme, singleItem }) => ({
    display: "grid",
    gridTemplateColumns: singleItem ? "1fr" : "1fr 1fr",
    gap: theme.spacing(1),
    width: "100%",
}));

const StyledTemplateItem = styled(
    Box,
    {
        shouldForwardProp: (prop) => prop !== "isSelected",
    }
)<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
    display: "flex",
    borderRadius: theme.kaltura.shape.roundness2,
    cursor: "pointer",
    border: `2px solid ${isSelected ? theme.palette.primary.main : "transparent"}`, // Double border technique: outer border for selection state (2px)
    boxShadow: isSelected ? "none" : `inset 0 0 0 1px ${theme.kaltura.palette.tone4}`, // inner border (via boxShadow) for constant outline (1px)
    "&:focus-visible": VisibleFocusStyle,
    padding: theme.spacing(2),
    gap: theme.spacing(0.5),
    "& > svg": {
        minWidth: "90px",
    },
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        flexDirection: "column",
        alignItems: "center",
        "& > svg": {
            minWidth: "72px",
        },
    },
}));

const StyledTemplateText = styled("div")(({ theme }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        textAlign: "center",
    },
}));

/**
 * Templates component - provides an accessible grid of templates for townhall creation
 */
export const TemplateSelection = (props: TemplateSelectionProps) => {
    const { labelSingle, labelMultiple, templates, illustrationsMap, defaultIllustration: DefaultIllustration } = props;
    const { control } = useFormContext();
    const templateRefs = useRef(new Map<string, HTMLDivElement>());
    const templateIds = templates.map((template) => template.id);

    const sendButtonAnalytics = useButtonAnalytics();

    /**
     * returns the appropriate illustration component based on template data
     */
    const getIllustration = (template: Template) => {
        // if the template defines an illustration, use it.
        if (template.illustration && illustrationsMap[template.illustration]) {
            const Illustration = illustrationsMap[template.illustration];
            return <Illustration />;
        }
        // fallback to a custom illustration if no specific one is defined
        return <DefaultIllustration />;
    };

    const handleTemplateClick = (template: Template, field: ControllerRenderProps<FieldValues, "roomMode">) => {
        sendButtonAnalytics(`${template.name}`, ButtonClickAnalyticsType.TOGGLE); // analytics context provides full event name
        field.onChange(template.id);
    };

    const handleTemplateKeyDown = (
        e: KeyboardEvent<HTMLDivElement>,
        index: number,
        field: ControllerRenderProps<FieldValues, "roomMode">
    ) => {
        let nextIndex: number;
        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                e.preventDefault();
                nextIndex = (index + 1) % templateIds.length;
                break;
            case "ArrowLeft":
            case "ArrowUp":
                e.preventDefault();
                nextIndex = (index - 1 + templateIds.length) % templateIds.length;
                break;
            case "Enter": {
                e.preventDefault();
                // Find the closest form and submit it
                const form = e.currentTarget.closest("form");
                if (form) {
                    form.requestSubmit();
                }
                return;
            }
            default:
                return;
        }
        const nextTemplate = templates[nextIndex];
        templateRefs.current.get(nextTemplate.id)?.focus();
        sendButtonAnalytics(nextTemplate.name, ButtonClickAnalyticsType.TOGGLE); // analytics context provides full event name
        field.onChange(nextTemplate.id);
    };

    return (
        <div>
            <StyledLabel text={templates.length > 1 ? labelMultiple : labelSingle} fontWeightBold={true} />
            <Controller
                name="roomMode"
                control={control}
                defaultValue={templateIds[0] || ""}
                render={({ field }) => (
                    <StyledGrid
                        className="kms-ds-create-townhall-modal-form-templates"
                        role="radiogroup"
                        aria-label={translate("Templates")}
                        singleItem={templates.length === 1}
                    >
                        {templates.map((template: Template, index: number) => (
                            <StyledTemplateItem
                                key={template.id}
                                ref={(element: HTMLDivElement | null) => {
                                    if (element) {
                                        templateRefs.current.set(template.id, element);
                                    }
                                }}
                                isSelected={templates.length > 1 && field.value === template.id}
                                onClick={() => handleTemplateClick(template, field)}
                                onKeyDown={(e) => handleTemplateKeyDown(e, index, field)}
                                role="radio"
                                aria-checked={field.value === template.id}
                                tabIndex={field.value === template.id ? 0 : -1}
                            >
                                {getIllustration(template)}
                                <StyledTemplateText>
                                    <Typography variant={"body2Highlight"}>{template.name}</Typography>
                                    <StyledDescription variant={"body2"}>{template.description}</StyledDescription>
                                </StyledTemplateText>
                            </StyledTemplateItem>
                        ))}
                    </StyledGrid>
                )}
            />
        </div>
    );
};
